import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GoldButton } from "../components/UI";
import { GoogleSignInButton, OrDivider } from "../components/GoogleSignIn";
import { Input } from "./Login";

export default function Register() {
  const [form, setForm] = useState({
    display_name: "", email: "", password: "", country_iso3: "GRC",
    city: "", legal_name: "", dob: "",
  });
  const [countries, setCountries] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginWithToken } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/meta").then(({ data }) => setCountries(data.countries)).catch(() => {});
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, region: form.city });
      await loginWithToken(data.token);
      nav("/dashboard");
    } catch (e2) {
      setErr(apiErr(e2, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-14 animate-fade-up">
      <div className="text-center mb-8">
        <BadgeCheck className="w-10 h-10 text-[#E5B842] mx-auto" />
        <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-slate-50 mt-3">Become an Arena Player</h1>
        <p className="text-slate-400 text-sm mt-1">Register → verification → WSTF Player ID (auto-approved, demo mode)</p>
      </div>
      <form onSubmit={submit} className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-6 space-y-4">
        <GoogleSignInButton label="Sign up with Google" country={form.country_iso3} testid="register-google-btn" />
        <p className="text-[11px] text-slate-500 -mt-2">Your WSTF ID uses the country selected below (default Greece).</p>
        <OrDivider />
        <Input label="Display name" value={form.display_name} onChange={set("display_name")} testid="reg-display-name" placeholder='e.g. Nikos "Apollo"' />
        <Input label="Legal name (private)" value={form.legal_name} onChange={set("legal_name")} testid="reg-legal-name" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Email" type="email" value={form.email} onChange={set("email")} testid="reg-email" />
          <Input label="Password" type="password" value={form.password} onChange={set("password")} testid="reg-password" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">Country</span>
            <select
              value={form.country_iso3}
              onChange={(e) => set("country_iso3")(e.target.value)}
              data-testid="reg-country"
              className="mt-1.5 w-full bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-[#E5B842]/50"
            >
              {countries.map((c) => (
                <option key={c.iso3} value={c.iso3}>{c.name}</option>
              ))}
            </select>
          </label>
          <Input label="City" value={form.city} onChange={set("city")} testid="reg-city" />
        </div>
        <Input label="Date of birth (private)" type="date" value={form.dob} onChange={set("dob")} testid="reg-dob" />
        {err && <div data-testid="reg-error" className="text-rose-400 text-sm">{err}</div>}
        <GoldButton type="submit" className="w-full" disabled={busy} data-testid="reg-submit">
          {busy ? "Creating credential…" : "Create my WSTF ID"}
        </GoldButton>
        <p className="text-center text-sm text-slate-400">
          Already a member? <Link to="/login" className="text-[#E5B842] font-semibold">Log in</Link>
        </p>
      </form>
    </div>
  );
}
